import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../services/api";

const ADMIN_KEY_STORAGE = "pennypantry_admin_api_key";

function AdminCard({ title, children }) {
  return (
    <section className="admin-monitor-card">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function JsonBlock({ data }) {
  return <pre className="admin-monitor-json">{JSON.stringify(data, null, 2)}</pre>;
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
}

export default function AdminMonitorPage() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [draftKey, setDraftKey] = useState(adminKey);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [payloads, setPayloads] = useState({});

  const headers = useMemo(() => ({ "X-Admin-Key": adminKey }), [adminKey]);

  const fetchAdmin = useCallback(
    async (path) => {
      const response = await fetch(`${API_BASE_URL}${path}`, { headers });
      if (!response.ok) {
        throw new Error(`${path} returned ${response.status}`);
      }
      return response.json();
    },
    [headers]
  );

  const refresh = useCallback(async () => {
    if (!adminKey) {
      setStatus("Enter your admin key to load monitoring.");
      return;
    }
    setLoading(true);
    setStatus("Loading production monitoring...");
    try {
      const [
        overview,
        errors,
        requests,
        health,
        featureFlags,
        providers,
        discovery,
        usersBasic,
      ] = await Promise.all([
        fetchAdmin("/admin/monitoring/overview"),
        fetchAdmin("/admin/monitoring/errors?limit=25"),
        fetchAdmin("/admin/monitoring/requests?limit=25"),
        fetchAdmin("/admin/monitoring/health"),
        fetchAdmin("/admin/monitoring/feature-flags"),
        fetchAdmin("/admin/monitoring/providers"),
        fetchAdmin("/admin/monitoring/discovery"),
        fetchAdmin("/admin/monitoring/users-basic"),
      ]);
      setPayloads({ overview, errors, requests, health, featureFlags, providers, discovery, usersBasic });
      setStatus(`Updated ${new Date().toLocaleTimeString()}`);
    } catch (error) {
      setStatus(error.message || "Could not load monitoring.");
    } finally {
      setLoading(false);
    }
  }, [adminKey, fetchAdmin]);

  useEffect(() => {
    if (adminKey) {
      refresh();
    }
  }, [adminKey, refresh]);

  const overview = payloads.overview || {};
  const recentErrors = payloads.errors?.errors || [];
  const requests = payloads.requests || {};
  const flags = payloads.featureFlags?.feature_flags || overview.feature_flags || {};

  return (
    <main className="admin-monitor-shell">
      <header className="admin-monitor-hero">
        <div>
          <p className="eyebrow">PennyPantry Production</p>
          <h1>Admin Monitor</h1>
          <p>Errors, latency, data health, providers and feature flags in one protected screen.</p>
        </div>
        <div className="admin-monitor-keybox">
          <input
            type="password"
            value={draftKey}
            placeholder="X-Admin-Key"
            onChange={(event) => setDraftKey(event.target.value)}
          />
          <button
            onClick={() => {
              localStorage.setItem(ADMIN_KEY_STORAGE, draftKey);
              setAdminKey(draftKey);
            }}
          >
            Save key
          </button>
          <button onClick={refresh} disabled={loading || !adminKey}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </header>

      <p className="admin-monitor-status">{status}</p>

      <section className="admin-monitor-grid admin-monitor-kpis">
        <AdminCard title="Status">
          <strong>{overview.status || "-"}</strong>
          <span>DB latency: {overview.db_latency_ms ?? "-"} ms</span>
          <span>Revision: {overview.alembic_revision || "-"}</span>
        </AdminCard>
        <AdminCard title="Errors 24h">
          <strong>{overview.errors_24h ?? "-"}</strong>
          <span>5xx: {overview.five_xx_24h ?? "-"}</span>
        </AdminCard>
        <AdminCard title="Data">
          <strong>{overview.prices_total ?? "-"}</strong>
          <span>Markets: {overview.markets_total ?? "-"}</span>
          <span>Products: {overview.products_total ?? "-"}</span>
        </AdminCard>
        <AdminCard title="Users">
          <strong>{payloads.usersBasic?.users_total ?? "-"}</strong>
          <span>Tokens: {payloads.usersBasic?.device_tokens_total ?? "-"}</span>
          <span>Push enabled: {payloads.usersBasic?.push_enabled_tokens ?? "-"}</span>
        </AdminCard>
      </section>

      <section className="admin-monitor-grid">
        <AdminCard title="Recent Errors">
          <div className="admin-monitor-list">
            {recentErrors.length ? recentErrors.map((item) => (
              <article key={item.id} className="admin-monitor-row">
                <strong>{item.level} {item.status_code || ""}</strong>
                <span>{item.method} {item.endpoint}</span>
                <small>{formatDate(item.created_at)}</small>
                <p>{item.message}</p>
              </article>
            )) : <p>No recent errors captured.</p>}
          </div>
        </AdminCard>

        <AdminCard title="Slow Endpoints">
          <div className="admin-monitor-list">
            {(requests.slow_endpoints || overview.slow_endpoints || []).map((item) => (
              <article key={`${item.method}-${item.endpoint}`} className="admin-monitor-row">
                <strong>{item.avg_duration_ms} ms</strong>
                <span>{item.method} {item.endpoint}</span>
                <small>{item.count} samples</small>
              </article>
            ))}
          </div>
        </AdminCard>

        <AdminCard title="Feature Flags">
          <JsonBlock data={flags} />
        </AdminCard>

        <AdminCard title="Provider Status">
          <JsonBlock data={payloads.providers?.providers || overview.providers || {}} />
        </AdminCard>

        <AdminCard title="Data Health">
          <JsonBlock data={payloads.health || {}} />
        </AdminCard>

        <AdminCard title="Discovery Jobs">
          <JsonBlock data={payloads.discovery || {}} />
        </AdminCard>

        <AdminCard title="Users Basic">
          <JsonBlock data={payloads.usersBasic || {}} />
        </AdminCard>

        <AdminCard title="Recent Requests">
          <JsonBlock data={(requests.requests || []).slice(0, 10)} />
        </AdminCard>
      </section>
    </main>
  );
}
